import { STORAGE_KEYS } from '../global/constants';
import { WorkspaceInterface, WorkspaceListCacheInterface } from '../interfaces/WorkspaceInterface';
import { LoginResponseItem } from '../mobx/interfaces/user';
import { UserModel } from '../mobx/models/user';
import { captureException } from '../services/logService';
import { BrandDomainDetailsInterface } from '../interfaces/SettingsInterface';
import { UserInterface } from '../interfaces/chatMessageInterface';

interface PaginationData {
  [key: string]: number; // Maps workspace_id to number of rows per page
}

interface UserPreferencesOptions {
  configName?: string;
  defaults: Record<string, unknown>;
}

let singleton: UserPreferenceSingleton | null | undefined = null;
const singletonEnforcer = Symbol();
const parseDataFile = (defaults: Record<string, unknown>): Record<string, unknown> => {
  try {
    const settings = localStorage.getItem(STORAGE_KEYS.USER_PREFERENCES);
    if (settings) return JSON.parse(settings);
    return {};
  } catch (error) {
    captureException(error);
    return defaults;
  }
};

const containsKey = (obj: object | null | undefined, key: PropertyKey): boolean =>
  ({}.hasOwnProperty.call(obj || {}, key));

class UserPreferences {
  defaults: Record<string, unknown>;
  data: Record<string, unknown>;
  constructor(opts: UserPreferencesOptions) {
    this.defaults = opts.defaults;
    this.data = parseDataFile(opts.defaults);
  }

  get(key: string, defaultValue: unknown) {
    if (containsKey(this.data, key)) {
      return this.data[key];
    }
    return defaultValue;
  }

  save(settings: Record<string, unknown>) {
    localStorage.setItem(STORAGE_KEYS.USER_PREFERENCES, JSON.stringify(settings));
  }

  set(key: string, value: unknown) {
    this.data = parseDataFile(this.defaults);
    this.data[key] = value;
    this.save(this.data);
  }

  remove(key: string) {
    delete this.data[key];
    this.save(this.data);
  }

  parseDataFile() {
    this.data = parseDataFile(this.defaults);
  }

  contains(key: string) {
    return Object.prototype.hasOwnProperty.call(this.data, key);
  }
}
export default class UserPreferenceSingleton {
  userPreferences: UserPreferences;
  static get CURRENT_USER() {
    return 'current_user';
  }
  static get WORKSPACE() {
    return 'workspace';
  }
  static get ONE_SIGNAL() {
    return 'onesignal';
  }

  static get BRANDING_DATA() {
    return 'branding_data';
  }

  static get OVERDUE_RESHEDULE_TIME() {
    return 'overdueTask_reshedule_time';
  }

  static get NOTIFICATION_RESHEDULE_TIME() {
    return 'notification_reshedule_time';
  }

  static get DESKTOP_NOTIFICATION_RESHEDULE_TIME() {
    return 'desktop_notification_reshedule_time';
  }

  static get MESSAGE_UNREAD_RESHEDULE_TIME() {
    return 'message_unread_reshedule_time';
  }

  static get FIREBASE_CUSTOM_TOKEN() {
    return 'firebase_custom_token';
  }

  static get CUSTOMER_PAGINATION() {
    return 'customer_pagination';
  }
  static get INVOICE_PAGINATION() {
    return 'invoice_pagination';
  }
  static get LAST_MESSAGE_GROUP() {
    return 'last_message_group';
  }
  static get MENTION_USERS() {
    return 'mention_users';
  }
  static get USER_USE_EMOJI() {
    return 'user_use_emoji';
  }

  static get RECENT_SEARCH() {
    return 'recent_search';
  }

  static get EXTRACTED_DESKTOP_VIEW() {
    return 'extracted_desktop_view';
  }

  static get THEME_MODE() {
    return 'theme_mode';
  }

  static get WORKSPACE_LIST_CACHE() {
    return 'workspace_list_cache';
  }

  constructor(enforcer: symbol) {
    if (enforcer !== singletonEnforcer) throw new Error('Cannot construct singleton');

    this.userPreferences = new UserPreferences({
      configName: 'user-preferences',
      defaults: {
        windowBounds: { width: 800, height: 600 }
      }
    });
  }

  static getInstance() {
    if (!singleton) {
      singleton = new UserPreferenceSingleton(singletonEnforcer);
    }
    return singleton;
  }

  static removeInstance() {
    singleton = undefined;
  }

  getWorkspace(): WorkspaceInterface | undefined {
    return this.userPreferences.get(UserPreferenceSingleton.WORKSPACE, undefined) as WorkspaceInterface | undefined;
  }

  setWorkspace(value: WorkspaceInterface | null) {
    return this.userPreferences.set(UserPreferenceSingleton.WORKSPACE, value);
  }

  getWorkspaceListCache(): WorkspaceListCacheInterface | undefined {
    return this.userPreferences.get(UserPreferenceSingleton.WORKSPACE_LIST_CACHE, undefined) as
      | WorkspaceListCacheInterface
      | undefined;
  }

  setWorkspaceListCache(value: WorkspaceListCacheInterface | null) {
    return this.userPreferences.set(UserPreferenceSingleton.WORKSPACE_LIST_CACHE, value);
  }

  setCurrentUser(value: LoginResponseItem | undefined) {
    return this.userPreferences.set(UserPreferenceSingleton.CURRENT_USER, value);
  }

  getCurrentUser(): UserModel {
    return this.userPreferences.get(UserPreferenceSingleton.CURRENT_USER, undefined) as UserModel;
  }

  setNotificationReschedule(value: string) {
    return this.userPreferences.set(UserPreferenceSingleton.NOTIFICATION_RESHEDULE_TIME, value);
  }

  getNotificationReschedule(): string | undefined {
    return this.userPreferences.get(UserPreferenceSingleton.NOTIFICATION_RESHEDULE_TIME, undefined) as string | undefined;
  }

  setDesktopNotificationReschedule(value: string) {
    return this.userPreferences.set(UserPreferenceSingleton.DESKTOP_NOTIFICATION_RESHEDULE_TIME, value);
  }

  getDesktopNotificationReschedule(): string | undefined {
    return this.userPreferences.get(UserPreferenceSingleton.DESKTOP_NOTIFICATION_RESHEDULE_TIME, undefined) as string | undefined;
  }

  setFirebaseCustomToken(workspaceId: string, value: string) {
    return this.userPreferences.set(`${UserPreferenceSingleton.FIREBASE_CUSTOM_TOKEN}_${workspaceId}`, value);
  }

  getFirebaseCustomToken(workspaceId: string) {
    return this.userPreferences.get(`${UserPreferenceSingleton.FIREBASE_CUSTOM_TOKEN}_${workspaceId}`, undefined);
  }

  getOneSignalInit() {
    return this.userPreferences.get(UserPreferenceSingleton.ONE_SIGNAL, undefined);
  }

  setOneSignalInit(value: boolean) {
    return this.userPreferences.set(UserPreferenceSingleton.ONE_SIGNAL, value);
  }

  getOverDueRescheduleTime(): Record<string, string> | undefined {
    const value = this.userPreferences.get(UserPreferenceSingleton.OVERDUE_RESHEDULE_TIME, undefined);
    return value as Record<string, string> | undefined;
  }

  setOverDueRescheduleTime(value: Record<string, string>) {
    return this.userPreferences.set(UserPreferenceSingleton.OVERDUE_RESHEDULE_TIME, value);
  }

  getMessageUnreadRescheduleTime(): Record<string, string> | undefined {
    const value = this.userPreferences.get(UserPreferenceSingleton.MESSAGE_UNREAD_RESHEDULE_TIME, undefined);
    return value as Record<string, string> | undefined;
  }

  setMessageUnreadRescheduleTime(value: Record<string, string>) {
    return this.userPreferences.set(UserPreferenceSingleton.MESSAGE_UNREAD_RESHEDULE_TIME, value);
  }

  get(key: string, defaultValue = null) {
    return this.userPreferences.get(key, defaultValue);
  }

  set(key: string, value: unknown) {
    this.userPreferences.set(key, value);
  }

  // Branding (workspace-scoped): branding-data.<workspaceId>
  getBranding(workspaceId: string) {
    if (!workspaceId) return undefined;
    const key = `${UserPreferenceSingleton.BRANDING_DATA}_${workspaceId}`;
    return this.userPreferences.get(key, undefined);
  }

  setBranding(workspaceId: string, value: BrandDomainDetailsInterface): void {
    if (!workspaceId) return;
    const key = `${UserPreferenceSingleton.BRANDING_DATA}_${workspaceId}`;
    this.userPreferences.set(key, value);
  }

  setCustomerPagination(value: PaginationData) {
    return this.userPreferences.set(UserPreferenceSingleton.CUSTOMER_PAGINATION, value);
  }

  getCustomerPagination(): PaginationData | undefined {
    return this.userPreferences.get(UserPreferenceSingleton.CUSTOMER_PAGINATION, undefined) as
      | PaginationData
      | undefined;
  }

  setInvoicePagination(value: PaginationData) {
    return this.userPreferences.set(UserPreferenceSingleton.INVOICE_PAGINATION, value);
  }

  getInvoicePagination() {
    return this.userPreferences.get(UserPreferenceSingleton.INVOICE_PAGINATION, undefined);
  }

  setLastMessageGroup(value: string) {
    return this.userPreferences.set(UserPreferenceSingleton.LAST_MESSAGE_GROUP, value);
  }
  getLastMessageGroup() {
    return this.userPreferences.get(UserPreferenceSingleton.LAST_MESSAGE_GROUP, undefined);
  }

  setMentionUsers(value: UserInterface[] | undefined) {
    return this.userPreferences.set(UserPreferenceSingleton.MENTION_USERS, value);
  }

  getMentionUsers() {
    return this.userPreferences.get(UserPreferenceSingleton.MENTION_USERS, undefined);
  }

  setUserUseEmoji(value: string[]) {
    return this.userPreferences.set(UserPreferenceSingleton.USER_USE_EMOJI, value);
  }

  getUserUseEmoji() {
    return this.userPreferences.get(UserPreferenceSingleton.USER_USE_EMOJI, undefined);
  }

  setRecentSearch(value: string[]) {
    return this.userPreferences.set(UserPreferenceSingleton.RECENT_SEARCH, value);
  }

  getRecentSearch() {
    return this.userPreferences.get(UserPreferenceSingleton.RECENT_SEARCH, undefined);
  }

  setExtractedDesktopView(value: number) {
    return this.userPreferences.set(UserPreferenceSingleton.EXTRACTED_DESKTOP_VIEW, value);
  }

  getExtractedDesktopView() {
    return this.userPreferences.get(UserPreferenceSingleton.EXTRACTED_DESKTOP_VIEW, undefined);
  }

  getThemeMode() {
    return this.userPreferences.get(UserPreferenceSingleton.THEME_MODE, undefined);
  }

  setThemeMode(value: number) {
    return this.userPreferences.set(UserPreferenceSingleton.THEME_MODE, value);
  }

  clearStoredUserData() {
    localStorage.removeItem(STORAGE_KEYS.USER_PREFERENCES);
  }
}
